import { createFileRoute } from '@tanstack/react-router';
import { BarChart3, List } from 'lucide-react';
import { requireFeature } from '@/common/utils/requireFeature';
import { FinanceTicketsDashboard } from '@/features/tech-systems/components/FinanceTicketsDashboard';
import { FinanceTicketsList } from '@/features/tech-systems/components/FinanceTicketsList';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { cn } from '@/lib/utils';

type FinanceSearch = {
  tab?: 'dashboard' | 'list';
  id?: string;
};

export const Route = createFileRoute(
  '/_authenticated/tech-systems/tickets/finance',
)({
  beforeLoad: () => requireFeature('TECH_SYSTEMS'),
  component: TechSystemsFinanceTicketsPage,
  validateSearch: (s: Record<string, unknown>): FinanceSearch => ({
    tab: (s.tab as FinanceSearch['tab']) || 'list',
    id: (s.id as string) || undefined,
  }),
});

function TechSystemsFinanceTicketsPage() {
  const { tab, id: highlightId } = Route.useSearch();
  const navigate = Route.useNavigate();

  const setTab = (next: 'dashboard' | 'list') => {
    navigate({
      search: (prev) => ({ ...prev, tab: next }),
    });
  };

  return (
    <PermissionGate permission={Permission.TECH_SYSTEMS_VIEW}>
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Financeiro — Sistemas TEC</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Fature tickets fechados no Conta Azul e acompanhe valores por período.
        </p>
      </div>

      <div className="flex gap-1 border-b">
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            tab === 'dashboard'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
          onClick={() => setTab('dashboard')}
        >
          <BarChart3 className="h-4 w-4" />
          Dashboard
        </button>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            tab === 'list'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
          onClick={() => setTab('list')}
        >
          <List className="h-4 w-4" />
          Lista de tickets
        </button>
      </div>

      {tab === 'dashboard' ? (
        <FinanceTicketsDashboard />
      ) : (
        <FinanceTicketsList highlightId={highlightId} />
      )}
    </div>
    </PermissionGate>
  );
}
